import {Form, Formik, FormikProps} from 'formik';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    Button, Card, DataWrapper, ExternalIntegrationPurposeSelect, InputFormik, PasswordInputFormik,
    PredefinedNotifierType, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {ExternalIntegrationPurpose, ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {DeepgramSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.DEEPGRAM;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<DeepgramSettings>;

const FIELDS = {
    ENABLED: 'enabled',
    STT_MODEL: 'sttModel',
    LANGUAGE: 'language',
    API_KEY: 'authentication.apiKey',
};

const FormTemplate: React.FC<FormTemplateProps> = ({loading, values, initialValues, submitForm, isValid}) => {
    const {t} = useTranslation();
    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Card>
                    <div className='tw-max-w-104'>
                        <ToggleFormik
                            name={FIELDS.ENABLED}
                            label={t(T_KEYS.LABELS.ENABLED)}
                            className='tw-my-4'
                        />
                        <InputFormik
                            name={FIELDS.STT_MODEL}
                            label={t(T_KEYS.LABELS.STT_MODEL)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        <InputFormik
                            name={FIELDS.LANGUAGE}
                            label={t(T_KEYS.LABELS.LANGUAGE)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        <PasswordInputFormik
                            name={FIELDS.API_KEY}
                            label={t(T_KEYS.LABELS.API_KEY)}
                            className='tw-w-full tw-mt-4'
                            spacing='bottom'
                            required={values.enabled}
                            autoComplete='one-time-code'
                        />

                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isValid}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </div>
                </Card>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const DeepgramSettingsPage: React.FC = () => {
    const {t} = useTranslation();
    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.STT);
    const [initialValues, setInitialValues] = useState<DeepgramSettings>({
        enabled: false,
        sttModel: '',
        language: '',
        authentication: {
            apiKey: ''
        }
    });

    const {
        send: fetchSettingsFn, data: settings, loading: fetchLoading
    } = useApiData(settingsApi.getDeepgram, {notificationType: PredefinedNotifierType.FetchError});

    const fetchSettings = useCallback(() => {
        fetchSettingsFn(integrationPurpose);
    }, [integrationPurpose, fetchSettingsFn]);

    const {send: updateSettings, loading: updateLoading} = useApiData(settingsApi.updateDeepgram,
        {onSuccess: fetchSettings, notificationType: PredefinedNotifierType.Save});

    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.STT_MODEL]: Yup.string().when(FIELDS.ENABLED, {
            is: true,
            then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        }),
        [FIELDS.LANGUAGE]: Yup.string().when(FIELDS.ENABLED, {
            is: true,
            then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        }),
        authentication: Yup.object().shape({
            apiKey: Yup.string().when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            })
        })
    }), [t]);

    const onSubmit = useCallback((values: DeepgramSettings) => {
        updateSettings(integrationPurpose, values);
    }, [integrationPurpose, updateSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        setInitialValues({
            enabled: !!settings?.enabled,
            sttModel: settings?.sttModel || '',
            language: settings?.language || '',
            authentication: {
                apiKey: settings?.authentication?.apiKey || ''
            }
        });
    }, [settings]);

    return (
        <Fragment>
            <ExternalIntegrationPurposeSelect
                className='tw-max-w-104 tw-mb-4'
                disabled
                value={integrationPurpose}
                onChange={setIntegrationPurpose}
            />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={onSubmit}
            >
                {(props) => <FormTemplate {...props} loading={fetchLoading || updateLoading} />}
            </Formik>
        </Fragment>

    );
};